import { useEffect, useRef, useState } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import ExpandedIcon from 'assets/icons/photo/ExpandedIcon'
import ExpansionIcon from 'assets/icons/photo/ExpansionIcon'
import { useTranslation } from 'react-i18next'
import Divider from '@material-ui/core/Divider'
import useMediaQuery from '@mui/material/useMediaQuery'
import SpyPointDatePicker from './SpyPointDatePicker'
import useSpyPointDateRange from '../core/useSpyPointDateRange'

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    marginLeft: -4,
    [theme.breakpoints.down('xs')]: {
      width: '-webkit-fill-available',
      marginLeft: -20,
    },
  },
  ButtonBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 5,
    // width: 'max-content',
    minWidth: 172,
    margin: '4px 10px 0px 4px',
    borderRadius: 5,
    padding: '0px 10px 0px 10px',
    backgroundColor: theme.palette.background.paper,
    color: '#fff',
    justifyContent: 'space-between',
    cursor: 'pointer',
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
      marginRight: -8,
      width: '-webkit-fill-available',
      minWidth: 'unset',
    },
  },
  divider: {
    marginLeft: 12,
    marginRight: 12,
    marginBottom: '0px !important',
    marginTop: '0px !important',
    color: theme.palette.background.paper,
    opacity: 0.25,
    borderBottom: `1px solid ${theme.palette.background.filter}`,
    [theme.breakpoints.down('xs')]: {
      marginLeft: 16,
      marginRight: 16,
    },
  },
  expansion: {
    marginTop: 16,
    marginLeft: 15,
  },
  datePickerContainer: {
    position: 'absolute',
    zIndex: 1001,
    marginTop: 4,
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    [theme.breakpoints.down('xs')]: {
      position: 'relative',
      width: '100%',
      opacity: 1,
      marginRight: -8,
    },
  },
  dateRangeText: {
    color: '#fff',
  },
}))

const SpyPointDateRangeFilterButton = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const buttonRef = useRef(null)
  const popoverRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [buttonWidth, setButtonWidth] = useState('auto')

  // Use the custom hook for date range functionality
  const dateRangeState = useSpyPointDateRange()

  // Initialize calendar view when opening the date picker
  useEffect(() => {
    dateRangeState.initializeCalendarView(open)
  }, [open])

  // Handle clicks outside the popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a Material-UI Select dropdown or menu item
      const isClickOnMenuPopover = !!event.target.closest('.MuiPopover-root') ||
                                   !!event.target.closest('.MuiMenu-root') ||
                                   !!event.target.closest('[role="listbox"]') ||
                                   !!event.target.closest('[role="presentation"]')

      if (
        open &&
        buttonRef.current &&
        popoverRef.current &&
        !buttonRef.current.contains(event.target) &&
        !popoverRef.current.contains(event.target) &&
        !isClickOnMenuPopover // Don't close if clicking on dropdown menu
      ) {
        handleClose()

        // Check if dateRange filter exists in selectedFilters, if not clear the picker
        // This fixes the issue where clicking outside would leave the temporary selection visible
        if (!dateRangeState.hasDateRangeFilter()) {
          dateRangeState.setStartDate(null)
          dateRangeState.setEndDate(null)
        }
      }
    }

    // Add event listener when popover is open
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      // Adding a small delay allows for proper positioning
      setTimeout(() => {
        if (popoverRef.current) {
          popoverRef.current.style.opacity = 1
        }
      }, 10)
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const handleButtonClick = () => {
    if (!open) {
      // When opening, set button width to match the popover (only on desktop)
      const isMobile = window.innerWidth <= 600
      if (!isMobile) {
        setButtonWidth('320px')
      }
    } else {
      // When closing, reset to auto width (only on desktop)
      const isMobile = window.innerWidth <= 600
      if (!isMobile) {
        setButtonWidth('auto')
      }
    }
    setOpen(!open)
  }

  const handleClose = () => {
    const isMobile = window.innerWidth <= 600
    if (!isMobile) {
      setButtonWidth('auto')
    }
    setOpen(false)
  }

  const handleApply = () => {
    if (dateRangeState.applyDateRange()) {
      handleClose()
    }
  }

  return (
    <Box data-testid="dateRange" className={classes.root}>
      <Box
        className={classes.ButtonBox}
        onClick={handleButtonClick}
        ref={buttonRef}
        style={{ width: window.innerWidth <= 600 ? '-webkit-fill-available' : buttonWidth }}
      >
        <Typography variant="body2" className={classes.dateRangeText}>
          { dateRangeState.formatDateRange(t) }
        </Typography>
        { open ? <ExpandedIcon className={classes.expansion} /> : <ExpansionIcon className={classes.expansion} /> }
      </Box>

      { isMobile && <Divider className={classes.divider} /> }

      { open && (
        <Box
          ref={popoverRef}
          className={classes.datePickerContainer}
          style={{ left: window.innerWidth <= 600 ? 0 : 'auto', opacity: 0 }}
        >
          <SpyPointDatePicker
            dateRangeState={dateRangeState}
            onApply={handleApply}
            onClear={() => dateRangeState.clearDateRange()}
          />
        </Box>
      ) }
    </Box>
  )
}

export default SpyPointDateRangeFilterButton
